import { AnalyticsService } from 'base/analytics';
import { LibraryCache } from 'base/library_cache';
import { PublicComponent } from 'base/library_service';
import { Button } from 'base/ui/button';
import { IconClear, IconSearch, IconShuffleAi, IconStars } from 'base/ui/icons';
import { InfiniteScroll, Loader, RenderItems } from 'base/ui/infinite_scroll';
import { FormInput } from 'base/ui/input';
import { Inline, Rows } from 'base/ui/layout';
import { PanelLayout } from 'base/ui/panel';
import { TextTinyLight } from 'base/ui/typography';
import { useDebouncedValue } from 'base/useDebounce';
import { observer } from 'mobx-react-lite';
import React from 'react';
import {
  ErrorState,
  LoadingResults,
  SearchNoResults,
  useResultComponents,
} from '../add_panel/component_results';
import { SingleSuggestionStore } from '../add_panel/suggestions';
import { Assistant } from '../ai/assistant';
import { ProjectEditingActions } from '../editing_actions/project';
import { selectionLocale, SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { SidebarNavigation } from '../sidebar/routes';
import styles from './wireframe_replace.css';

export const ReplaceComponentPanel = observer(function _ReplaceComponentPanel({
  navigation,
  selection,
  session,
  library,
  assistant,
  analytics,
  editing,
}: {
  navigation: SidebarNavigation;
  selection: SelectionStore;
  session: ProjectSession;
  library: LibraryCache;
  assistant: Assistant;
  analytics: AnalyticsService;
  editing: ProjectEditingActions;
}) {
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const { ComponentResult } = useResultComponents(
    selection,
    session,
    library,
    assistant,
    analytics,
    React.useState(new SingleSuggestionStore())[0],
  );
  const sel = selection.get();

  React.useEffect(
    () => {
      if (sel.section == null) navigation.backOrClose();
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sel.section],
  );

  const clearSearch = React.useCallback(() => {
    setSearch('');
  }, []);

  const section = sel.section
    ? sel.section.type === 'inline'
      ? sel.section.value
      : selection.project.globalSections.get(sel.section.id)
    : undefined;

  const searchLoader = React.useCallback<Loader<PublicComponent>>(
    async (continuation) => {
      if (!section || !sel.page) return {};
      let res;
      if (debouncedSearch) {
        res = await library.findPublicComponents(
          {
            filterKind: 'component',
            search: debouncedSearch,
            excludeAppComponents: true,
          },
          continuation,
        );
      } else {
        const category = await assistant.getBlocksCategoryForSection(
          selectionLocale(sel, selection.project),
          sel.page.state,
          section.state,
        );
        res = await library.findPublicComponents(
          category
            ? {
                // Even though we're showing these results to the user, we use the
                // smaller AI component pool.  Adam's request.  I think he is
                // prioritizing the most relevant results.
                filterBlocksCategoryAi: [category],
                filterKind: 'component',
              }
            : {
                // fallback to feature sections
                filterKind: 'component',
                filterCategory: ['feature-sections'],
              },
          continuation,
        );
      }
      return {
        items: res.components,
        continuation: res.continuation || undefined,
      };
    },
    [section, sel, debouncedSearch, library, assistant, selection.project],
  );

  const searchRender = React.useCallback<RenderItems<PublicComponent>>(
    (items) => {
      if (items.length === 0) {
        return <SearchNoResults clearSearch={clearSearch} subject="section" />;
      }
      return (
        <Rows spacing={8}>
          {items.map((c) => (
            <ComponentResult
              key={c.componentSlug}
              component={c}
              interaction="replace"
              onUsed={navigation.back}
              analyticsContext={{
                search,
                categoryName: 'replace-component',
              }}
            />
          ))}
        </Rows>
      );
    },
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <PanelLayout
      title="Replace Component"
      navigation={navigation}
      header={
        <FormInput
          type="search"
          placeholder="Search"
          value={search}
          onChange={setSearch}
          iconStart={IconSearch}
          buttonEnd={
            search && (
              <Button
                size="small"
                variant="subtle"
                icon={IconClear}
                onClick={clearSearch}
              />
            )
          }
        />
      }
      footer={
        <Button
          variant="secondary"
          label="Shuffle component"
          iconColor="ai"
          icon={IconShuffleAi}
          fullWidth={true}
          onClick={() => {
            const selectedSection = selection.get().section;
            if (selectedSection == null) {
              return;
            }
            editing.shuffleSectionComponent(selectedSection);
          }}
        />
      }
    >
      <Rows spacing={16}>
        {!search && (
          <Inline spacing={8}>
            <div className={styles.suggestedComponentsIcon}>
              <IconStars />
            </div>
            <TextTinyLight color="grey">Suggested components</TextTinyLight>
          </Inline>
        )}
        <InfiniteScroll
          loader={searchLoader}
          render={searchRender}
          LoadingState={LoadingResults}
          ErrorState={ErrorState}
        />
      </Rows>
    </PanelLayout>
  );
});
